/**
 * Formateurs de date
 */

import { toDate } from './DateExtensions';
import { format, isToday, isTomorrow, differenceInDays, differenceInMinutes, differenceInHours, differenceInWeeks, isSameYear, isSameMonth } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import i18next from 'i18next';

const locales = {
  fr,
  'fr-FR': fr,
  en: enUS,
  'en-US': enUS
};

/**
 * Récupère la locale date-fns pour la langue courante
 * Gère les codes avec tiret (fr-FR) et fallback vers locales.en
 */
const getLocale = () => {
  const lang = i18next.language || '';
  // Essayer la langue exacte (fr-FR), puis la base (fr), puis fallback en
  return locales[lang] || locales[lang.split('-')[0]] || locales.en;
};

/**
 * Capitalise la première lettre d'une chaîne
 * Utilisé pour les jours/mois retournés par date-fns en minuscule
 */
const capitalize = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Options de formatage par défaut
 */
export const DEFAULT_FORMAT_OPTIONS = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
};

/**
 * Formate une date en format français lisible
 * @param {Date|string|Object} date - Date à formater
 * @param {Intl.DateTimeFormatOptions} options - Options de formatage
 * @returns {string} Date formatée ou chaîne par défaut si invalide
 */
export const formatDate = (date, formatStr = 'dd/MM/yyyy') => {
  if (!date) return '';
  
  try {
    // Utiliser toDate() pour gérer tous les formats (Date, ISO, Timestamp, plain object)
    const dateObj = toDate(date);
    if (!dateObj) return '';
    
    const locale = getLocale();
    
    return format(dateObj, formatStr, { locale });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Formate une date relative pour le PASSÉ (il y a X temps)
 * Utilisé pour les notifications et événements passés
 * @param {Date|string|Object} date - Date à formater
 * @returns {string}
 */
export const formatRelativeDate = (date) => {
  const converted = toDate(date);
  if (!converted) return '';
  
  const now = new Date();
  const locale = getLocale();
  
  // Calculer les différences
  const diffInMinutes = differenceInMinutes(now, converted);
  const diffInHours = differenceInHours(now, converted);
  const diffInDays = differenceInDays(now, converted);
  const diffInWeeks = differenceInWeeks(now, converted);
  
  // Moins d'une minute
  if (diffInMinutes < 1) {
    return i18next.t('date.just_now');
  }
  
  // Moins d'une heure
  if (diffInMinutes < 60) {
    return i18next.t('date.ago_minutes', { count: diffInMinutes });
  }
  
  // Moins de 24h
  if (diffInHours < 24) {
    return i18next.t('date.ago_hours', { count: diffInHours });
  }
  
  // Moins de 7 jours
  if (diffInDays < 7) {
    return i18next.t('date.ago_days', { count: diffInDays });
  }
  
  // Moins de 30 jours
  if (diffInDays < 30) {
    return i18next.t('date.ago_weeks', { count: diffInWeeks });
  }
  
  // Plus de 30 jours : afficher la date complète
  return format(converted, 'd MMMM yyyy', { locale });
}; 

// Nouvelle fonction pour le format mois année
export const formatMonthYear = (date) => {
  try {
    if (!date) return '';
    
    // Utiliser toDate() pour gérer tous les formats
    const dateObj = toDate(date);
    if (!dateObj) return '';
    
    const locale = getLocale();
    
    // Utiliser date-fns pour un formatage cohérent avec le reste
    return format(dateObj, 'MMMM yyyy', { locale });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}; 

/**
 * Formate une date d'activité FUTURE de manière intelligente et contextuelle
 * Style réseaux sociaux avec affichage adapté selon la distance temporelle
 * @param {Date|string|Object} date - Date à formater
 * @returns {string} Date formatée de manière contextuelle
 */
export const formatActivityDate = (date) => {
  if (!date) return '';
  
  try {
    // Utiliser toDate() pour gérer tous les formats (Date, ISO string, Timestamp, plain object)
    const dateObj = toDate(date);
    if (!dateObj) return '';
    
    const locale = getLocale();
    const now = new Date();
    
    // Calculer la différence en jours (positif pour le futur, négatif pour le passé)
    const daysDifference = differenceInDays(dateObj, now);
    
    // EDGE CASE: Si la date est passée (même jour mais heure passée), utiliser formatRelativeDate
    // differenceInDays retourne 0 pour le même jour, donc on vérifie aussi les millisecondes
    if (daysDifference < 0 || (daysDifference === 0 && dateObj < now)) {
      return formatRelativeDate(dateObj);
    }
    
    // Aujourd'hui
    if (isToday(dateObj)) {
      const timeString = format(dateObj, 'HH:mm', { locale });
      return i18next.t('date.today_at', { time: timeString });
    }
    
    // Demain
    if (isTomorrow(dateObj)) {
      const timeString = format(dateObj, 'HH:mm', { locale });
      return i18next.t('date.tomorrow_at', { time: timeString });
    }
    
    // Prochains jours (2-5 jours)
    // EDGE CASE: Si changement de mois, afficher la date complète
    if (daysDifference >= 2 && daysDifference <= 5) {
      const dayOfWeek = capitalize(format(dateObj, 'EEEE', { locale }));
      const timeString = format(dateObj, 'HH:mm', { locale });
      
      // Si différent mois, afficher "Vendredi 2 nov. à 14h30"
      if (!isSameMonth(dateObj, now)) {
        const dayMonth = format(dateObj, 'd MMM', { locale });
        return i18next.t('date.day_month_at', { day: dayOfWeek, dayMonth, time: timeString });
      }
      
      // Même mois: "Vendredi à 14h30"
      return i18next.t('date.day_at', { day: dayOfWeek, time: timeString });
    }
    
    // Semaine prochaine et au-delà jusqu'à 13 jours
    if (daysDifference >= 6 && daysDifference <= 13) {
      const dayOfWeek = capitalize(format(dateObj, 'EEEE', { locale }));
      const dayMonth = format(dateObj, 'd MMM', { locale });
      const timeString = format(dateObj, 'HH:mm', { locale });
      return i18next.t('date.day_month_at', { day: dayOfWeek, dayMonth, time: timeString });
    }
    
    // 14-30 jours - affiche jour + mois complet
    if (daysDifference >= 14 && daysDifference <= 30) {
      const day = format(dateObj, 'd', { locale });
      const month = format(dateObj, 'MMMM', { locale });
      const timeString = format(dateObj, 'HH:mm', { locale });
      return i18next.t('date.date_month_at', { day, month, time: timeString });
    }
    
    // Plus loin (>30 jours)
    // Si même année, pas besoin d'afficher l'année
    if (isSameYear(dateObj, now)) {
      const day = format(dateObj, 'd', { locale });
      const month = format(dateObj, 'MMMM', { locale });
      const timeString = format(dateObj, 'HH:mm', { locale });
      return i18next.t('date.date_month_at', { day, month, time: timeString });
    }
    
    // Année différente : afficher l'année
    const day = format(dateObj, 'd', { locale });
    const month = format(dateObj, 'MMMM', { locale });
    const year = format(dateObj, 'yyyy', { locale });
    const timeString = format(dateObj, 'HH:mm', { locale });
    return i18next.t('date.date_month_year_at', { day, month, year, time: timeString });
    
  } catch (error) {
    console.error('Error formatting activity date:', error, date);
    return '';
  }
};

/**
 * Format une date ISO en format relatif (aujourd'hui, hier, etc.)
 * @deprecated Utiliser formatActivityDate pour les activités futures ou formatRelativeDate pour le passé
 * @param {string|Date|Object} date - Date à formater
 * @returns {string} Date formatée de manière relative
 */
export const formatDateToRelative = (date) => {
  // Pour compatibilité, on redirige vers formatActivityDate
  return formatActivityDate(date);
}; 